import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../config/api';
import type { RoomPayHistoryRecord } from '../utils/rentalPaymentHistory';

export interface ManageRoomPayInfo {
  property_id: number;
  title: string;
  room_number?: string | null;
  pay_room_code?: string | null;
  pay_url?: string | null;
  qr_api_url?: string | null;
  has_pay_qr: boolean;
}

export interface RoomPayCheckout {
  pay_room_code: string;
  page_status: 'unpaid' | 'no_invoice' | 'no_tenant';
  room: {
    pay_room_code: string;
    pay_url?: string | null;
    property_id: number;
    title: string;
    room_number?: string | null;
    type: string;
    full_address: string;
    status: string;
    main_image?: string | null;
    price?: number | null;
  };
  tenant?: { name: string } | null;
  invoice?: {
    billing_period_start?: string | null;
    billing_period_end?: string | null;
    due_date?: string | null;
    rent: number;
    electricity: number;
    water: number;
    services: number;
    other: number;
    total: number;
    currency: string;
  } | null;
}

export interface RoomPayPaymentResult {
  transaction_id: string;
  merchant_order_code: string;
  order_code?: string | null;
  sepay_order_code?: string | null;
  payment_link_id: string;
  checkout_url?: string | null;
  checkoutUrl?: string | null;
  qr_code_url?: string | null;
  qrCode?: string | null;
  qrUrl?: string | null;
  amount: number;
  status: string;
  pay_room_code: string;
  bank?: string | null;
  account_number?: string | null;
  accountNumber?: string | null;
  account_name?: string | null;
  code?: string | null;
}

export interface ContractRequestPayload {
  tenant_name: string;
  tenant_phone: string;
  tenant_cccd?: string;
  tenant_date_of_birth?: string;
  tenant_address?: string;
  rental_duration: number;
}

export interface ContractRequestResult {
  success: boolean;
  message: string;
  property_id: number;
  pay_room_code: string;
}

export const roomPayApi = {
  getCheckout: async (roomCode: string): Promise<RoomPayCheckout> => {
    const response = await axios.get(
      `${API_BASE_URL}/pay/rooms/${encodeURIComponent(roomCode)}/checkout`
    );
    return response.data;
  },

  createPayment: async (roomCode: string): Promise<RoomPayPaymentResult> => {
    const response = await axios.post(
      `${API_BASE_URL}/pay/rooms/${encodeURIComponent(roomCode)}/pay`,
      {}
    );
    return response.data;
  },

  submitContractRequest: async (
    roomCode: string,
    payload: ContractRequestPayload,
  ): Promise<ContractRequestResult> => {
    const response = await axios.post(
      `${API_BASE_URL}/pay/rooms/${encodeURIComponent(roomCode)}/contract-request`,
      payload,
    );
    return response.data;
  },

  getManageInfo: async (propertyId: number): Promise<ManageRoomPayInfo> => {
    const response = await axios.get(`${API_BASE_URL}/pay/manage/rooms/${propertyId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  autoCode: async (propertyId: number): Promise<ManageRoomPayInfo> => {
    const response = await axios.post(
      `${API_BASE_URL}/pay/manage/rooms/${propertyId}/auto-code`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getQrImageUrl: (roomCode: string) =>
    `${API_BASE_URL}/pay/rooms/${encodeURIComponent(roomCode)}/qr.png`,

  /** Lịch sử thanh toán tiền trọ SePay — khoản thu của chủ nhà */
  getPaymentHistory: async (): Promise<RoomPayHistoryRecord[]> => {
    const response = await axios.get(`${API_BASE_URL}/pay/manage/payment-history`, {
      headers: getAuthHeaders(),
    });
    const payload = response.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  },
};
